// #FLEET_OWNER_TILES_SHARED_V1 (2026-10-08) — Bill: "YARD (Jerry) & Fleet (Bob) to get these tiles that are
// currently in Owners Dash: Rental / RPO, Idle Equipment, Units down for Repair."
// Same data the Owner Dash reads (fleet-summary digest + the daily fleet-inshop-snapshot), same rules
// (Idle = 30+ days, works but not earning; Down = in the shop, days down), drawn in whichever dashboard's
// colors are passed in. Each tile opens the matching Fleet section.
import React, { useEffect, useState } from 'react';

const money = (n) => (n == null || isNaN(+n)) ? '—' : '$' + Math.round(+n).toLocaleString();
const labelOf = (u) => {
  const d = String(u.desc || '').replace(new RegExp('^\\s*' + String(u.unit || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*[-·:]?\\s*', 'i'), '').trim();
  return d ? `${u.unit} · ${d}` : String(u.unit || '');
};

// #TILE_LOC_V1 (2026-10-13) — Bill: "add location on unit in IDLE & Units Down right in the middle here on these tiles,
// viewable by phone." locOf(unit) comes from the parent (FleetModule joins unit → VIN → latest GPS fix, the same
// fleet-locations record the Daily sheet's GPS column reads). Returns { text, url, title, moving, old, yard } or null.
// No tracker = the middle stays empty rather than guessing.
// #FLEET_WO_TILE_V1 (2026-10-14) — extraTile: an optional 4th tile in the same row (Bob's work-order queue on the Fleet dash).
// It gets this row's own tile / title / list / row helpers so it looks like a sibling. The Owner Dash passes nothing.
// #FLEET_WO_LAYOUT_V1 (2026-10-14) — hideRental: leave the Rental / RPO tile out of the row; only="rental": render just that tile
// (Bob's dash shows it at the right end of the Telematics row, where a static reference tile belongs).
export default function FleetOwnerTiles({ sg, T = {}, onOpen, onOpenUnit, locOf, extraTile, hideRental, only, maxH }) {
  const C = { card:'#11161F', line:'#1C2637', row:'#161C28', text:'#E5E7EB', muted:'#94A3B8', link:'#38BDF8', radius:14,
    glowRental:'#E7B73B', glowIdle:'#E7B73B', glowDown:'#FB923C', ...T };
  const [sum, setSum] = useState(null);
  const [shop, setShop] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try { const f = await sg('fleet-summary'); if (alive) setSum(f && typeof f === 'object' ? f : null); } catch {}
      try { const s = await sg('fleet-inshop-snapshot'); if (alive) setShop(s && typeof s === 'object' ? s : null); } catch {}
    })();
    return () => { alive = false; };
  }, [sg]);

  const tile = (glow) => ({ background:C.card, border:`1px solid ${C.line}`, borderTop:`3px solid ${glow}`, borderRadius:C.radius,
    padding:'12px 14px', display:'flex', flexDirection:'column', minHeight:0, maxHeight:330,
    boxShadow:`0 0 22px -8px ${glow}AA, inset 0 1px 0 #ffffff08` });
  const title = { fontSize:11, fontWeight:900, letterSpacing:1, textTransform:'uppercase', color:C.text, marginBottom:6, cursor:'pointer' };
  const sub = { textTransform:'none', letterSpacing:0, fontWeight:600, color:C.muted };
  const list = { flex:1, minHeight:0, overflowY:'auto', paddingRight:6 };
  const locCellEl = (unit) => {   // #TILE_LOC_V1 — the middle column
    const L = locOf ? locOf(unit) : null;
    if (!L || !L.text) return <span style={{ flex:'0 1 34%', minWidth:0 }} />;
    const tx = (L.moving ? '▸ ' : '📍 ') + L.text;
    const st = { flex:'0 1 34%', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:10.5, fontWeight:600,
      color: L.moving ? '#4ADE80' : L.yard ? C.muted : C.link, opacity: L.old ? .65 : 1, textAlign:'left', textDecoration:'none' };
    return L.url
      ? <a href={L.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title={L.title} style={st}>{tx}</a>
      : <span title={L.title} style={st}>{tx}</span>;
  };
  const row = (k, left, right, c, click, unit) => (
    <div key={k} onClick={click} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, padding:'4px 0', borderBottom:`1px solid ${C.row}`,
      fontSize:11.5, minWidth:0, cursor: click ? 'pointer' : 'default' }}>
      <span style={{ color:C.text, flex:'1 1 auto', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{left}</span>
      {locOf && unit != null && locCellEl(unit)}
      <span style={{ color:c || C.muted, fontWeight:800, whiteSpace:'nowrap', fontVariantNumeric:'tabular-nums' }}>{right}</span>
    </div>);
  const empty = (txt, good) => <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', textAlign:'center',
    fontSize:11.5, color: good ? '#34D399' : C.muted, padding:'10px 0' }}>{txt}</div>;
  const dayMs = 86400000;

  // Rental / RPO
  const rr = sum && sum.rentalRpo; const rItems = rr && Array.isArray(rr.items) ? rr.items : [];
  // Idle
  const idle = sum && Array.isArray(sum.idle) ? sum.idle : []; const idleBar = (sum && sum.idleTarget) || 30;
  const sumAt = sum && sum.at ? new Date(sum.at) : null; const sumAge = sumAt ? Math.round((Date.now() - sumAt) / dayMs) : null;
  // Down for repair (daily snapshot, falls back to the live summary like the Owner Dash)
  const src = shop || sum; const down = src && Array.isArray(src.shop) ? src.shop : [];
  const srcAt = src && src.at ? new Date(src.at) : null;

  const rentalTile = (
      <div style={{ ...tile(C.glowRental), ...(maxH ? { maxHeight: maxH, height: '100%', boxSizing: 'border-box' } : {}) }}>
        <div style={title} onClick={() => onOpen && onOpen('rental')}>Rental / RPO <span style={sub}>{rr ? `· ${rr.count || rItems.length} on the list` : ''}</span></div>
        {!sum ? empty('No scan yet — open Fleet → Rental/RPO to publish this.') : !rItems.length ? empty('Nothing on the Rental/RPO list right now.', true) : (
          <div style={list}>{rItems.map((x, i) => (
            <div key={i} onClick={() => onOpen && onOpen('rental')} style={{ padding:'5px 2px', borderBottom:`1px solid ${C.row}`, fontSize:11.5, cursor:'pointer' }}>
              <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                <span style={{ color:C.text, fontWeight:700, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{x.unit} <span style={{ color:C.muted, fontWeight:600 }}>{x.desc || ''}</span></span>
                <span style={{ fontWeight:800, color: x.type === 'RPO' ? '#E7B73B' : C.muted }}>{x.type || 'Rental'}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', color:C.muted, fontSize:10.5, marginTop:1 }}>
                <span>Start {x.rentalStart || '—'}</span><span style={{ fontVariantNumeric:'tabular-nums' }}>{money(x.monthlyRate)}/mo · {money(x.weeklyRate)}/wk</span>
              </div>
            </div>))}
          </div>)}
      </div>
  );
  if (only === 'rental') return rentalTile;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap:12, alignItems:'stretch' }}>
      {!hideRental && rentalTile}

      <div style={tile(C.glowIdle)}>
        <div style={title} onClick={() => onOpen && onOpen('idle')}>Idle Equipment <span style={sub}>· {idleBar}+ days · works, not earning</span></div>
        {!sum ? empty('No scan yet — run the Idle 30 scan in Fleet.') : (<>
          <div style={{ display:'flex', alignItems:'baseline', gap:8, margin:'2px 0 6px' }}>
            <span style={{ fontSize:26, fontWeight:900, color: idle.length ? '#E7B73B' : '#34D399' }}>{sum.idleCount || 0}</span>
            <span style={{ fontSize:11, color:C.muted }}>units · {money(sum.idleBurn || 0)}/day standing still</span>
          </div>
          {idle.length ? <div style={list}>{idle.slice(0, 20).map(u => row(u.unit, labelOf(u), `${u.proven ? '' : '≥'}${u.days}d`, u.days >= 180 ? '#F87171' : '#E7B73B',
              onOpenUnit ? () => onOpenUnit(u.unit, 'idle') : (onOpen ? () => onOpen('idle') : undefined), u.unit))}</div>
            : empty(`✓ Nothing idle past ${idleBar} days.`, true)}
          <div style={{ fontSize:9.5, color:C.muted, marginTop:6 }}>as of {sumAt ? sumAt.toLocaleDateString() : '—'}{sumAge != null && sumAge > 7 ? ` · ${sumAge}d old, re-scan in Fleet` : ''}</div>
        </>)}
      </div>

      <div style={tile(C.glowDown)}>
        <div style={title} onClick={() => onOpen && onOpen('inshop')}>Units Down for Repair</div>
        {!src ? empty('No snapshot yet.') : (<>
          <div style={{ display:'flex', alignItems:'baseline', gap:8, margin:'2px 0 6px', flexWrap:'wrap' }}>
            <span style={{ fontSize:26, fontWeight:900, color: down.length ? '#FB923C' : '#34D399' }}>{src.shopCount || 0}</span>
            <span style={{ fontSize:11, color:C.muted }}>units · {money(src.shopBurn || 0)}/day not working</span>
            {src.uptimePct != null && <span style={{ marginLeft:'auto', fontSize:11, fontWeight:900, border:`1px solid ${C.line}`, borderRadius:20, padding:'2px 9px',
              color: src.uptimePct >= 95 ? '#34D399' : src.uptimePct >= 90 ? '#E7B73B' : '#F87171' }}>{src.uptimePct}% uptime</span>}
          </div>
          {down.length ? <div style={list}>{down.slice(0, 20).map(u => row(u.unit, labelOf(u), `${u.days}d down`, u.days >= 30 ? '#F87171' : u.days >= 14 ? '#FB923C' : C.muted,
              onOpenUnit ? () => onOpenUnit(u.unit, 'inshop') : (onOpen ? () => onOpen('inshop') : undefined), u.unit))}</div>
            : empty('✓ Nothing sitting in the shop.', true)}
          <div style={{ fontSize:9.5, color:C.muted, marginTop:6 }}>Daily snapshot · as of {srcAt ? srcAt.toLocaleDateString() : '—'}</div>
        </>)}
      </div>
      {extraTile ? extraTile({ tile, title, sub, list, row, empty, C }) : null}
    </div>
  );
}
